import React from 'react';
import styled from 'styled-components';
import LinearProgress from './LinearProgress';
import {
  ProcessableFileUploadGroups,
  ProcessingFileUpload,
  getFilesInflection,
} from '../lib/file-upload';
import FileWorkflowStep, {
  FileWorkflowStepType,
  FileWorkflowActon,
  FileWorkflowStepConsumableProps,
} from './FileWorkflowStep';

function getUploadProgress(
  processingFiles: Array<ProcessingFileUpload>,
  failedCount: number,
  completedCount: number
) {
  const finishedCount = failedCount + completedCount;
  const finishedProgress = finishedCount * 100;
  const fileCount = processingFiles.length + finishedCount;
  const uploadProgress = processingFiles.reduce(
    (uploadProgress, fileUpload) => uploadProgress + fileUpload.progress,
    finishedProgress
  );

  return Math.floor(uploadProgress / fileCount) || 0;
}

function getDoneAction(onDone: () => void): FileWorkflowActon {
  return {
    action: onDone,
    description: 'Done',
  };
}

function getAddStep(): FileWorkflowStepConsumableProps {
  return {
    stepType: FileWorkflowStepType.Add,
  };
}

function getPendingStep(
  fileCount: number,
  onUploadPending: () => void
): FileWorkflowStepConsumableProps {
  const fileInflection = getFilesInflection(fileCount);

  return {
    stepType: FileWorkflowStepType.Pending,
    primaryAction: {
      action: onUploadPending,
      description: `Upload ${fileCount} ${fileInflection}`,
    },
  };
}

function getProcessingStep(
  uploadProgress: number
): FileWorkflowStepConsumableProps {
  return {
    stepType: FileWorkflowStepType.Processing,
    description: `Uploading: ${uploadProgress}% complete`,
  };
}

function getFailedStep(
  failedCount: number,
  completedCount: number,
  onRetry: () => void,
  onDone?: () => void
): FileWorkflowStepConsumableProps {
  const fileCount = failedCount + completedCount;
  const fileInflection = getFilesInflection(failedCount);
  const retryAction: FileWorkflowActon = {
    action: onRetry,
    description: `Retry ${failedCount} ${fileInflection}`,
  };
  const primaryAction =
    completedCount > 0 && !!onDone ? getDoneAction(onDone) : retryAction;
  const secondaryAction =
    primaryAction !== retryAction ? retryAction : undefined;

  return {
    stepType: FileWorkflowStepType.Failed,
    description: `${failedCount}/${fileCount} Uploads failed`,
    primaryAction,
    secondaryAction,
  };
}

function getCompleteStep(onDone?: () => void): FileWorkflowStepConsumableProps {
  return {
    stepType: FileWorkflowStepType.Complete,
    description: 'Complete',
    primaryAction: onDone ? getDoneAction(onDone) : undefined,
  };
}

const StyledLinearProgress = styled(LinearProgress)`
  margin: 1rem 0;
`;

export type UploadProgressProps = {
  uploadProgress: number;
};

export type RenderWorkflowProps = FileWorkflowStepConsumableProps &
  UploadProgressProps;

export type FileWorkflowManagerConsumableProps = {
  /** Callback when all files are finished uploading. Call your backend here to persist the document */
  onDone?: () => void;
  /** Allows for custom rendering file workflow manager to override the default layout */
  renderWorkflow?: (props: RenderWorkflowProps) => JSX.Element | null;
};

export type Props = {
  onUploadPending: () => void;
  onRetry: () => void;
} & ProcessableFileUploadGroups &
  FileWorkflowManagerConsumableProps;

export default function FileWorkflowManager({
  pendingFiles,
  processingFiles,
  failedFiles,
  completedFiles,
  onUploadPending,
  onRetry,
  onDone,
  renderWorkflow,
}: Props) {
  const uploadProgress = getUploadProgress(
    processingFiles,
    failedFiles.length,
    completedFiles.length
  );
  function getWorkflowStep() {
    if (pendingFiles.length > 0) {
      return getPendingStep(pendingFiles.length, onUploadPending);
    }

    if (processingFiles.length > 0) {
      return getProcessingStep(uploadProgress);
    }

    if (failedFiles.length > 0) {
      return getFailedStep(
        failedFiles.length,
        completedFiles.length,
        onRetry,
        onDone
      );
    }

    if (completedFiles.length > 0) {
      return getCompleteStep(onDone);
    }

    return getAddStep();
  }
  const workflowStep = getWorkflowStep();

  return renderWorkflow ? (
    renderWorkflow({ ...workflowStep, uploadProgress })
  ) : workflowStep.stepType !== FileWorkflowStepType.Add ? (
    <React.Fragment>
      <StyledLinearProgress progress={uploadProgress} />
      <FileWorkflowStep {...workflowStep} />
    </React.Fragment>
  ) : null;
}
